import { Send } from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { sendAdhocNotification } from "@/features/zdalny-dostep/notification-schedules-api";
import { encodeScope } from "@/lib/scope";
import { useHelpdeskMutation } from "./api";

/** Ważna dobę — stanowisko offline dostanie ją po połączeniu. */
const VALIDITY_HOURS = 24;
const BODY_MAX = 2000;

/**
 * Wiadomość na ekran jednego stanowiska (powiadomienie Device Agenta, jak wysyłka
 * ad-hoc z „Powiadomień”, zawężona do tego urządzenia w obrębie apteki).
 */
export function DeviceMessageButton({
	deviceUuid,
	idfirm,
	name,
}: {
	deviceUuid: string;
	idfirm: number;
	name: string;
}) {
	const formId = useId();
	const [open, setOpen] = useState(false);
	const [title, setTitle] = useState("Dział IT");
	const [body, setBody] = useState("");
	const send = useHelpdeskMutation(
		() =>
			sendAdhocNotification({
				title: title.trim(),
				body: body.trim(),
				validityHours: VALIDITY_HOURS,
				canaryOnly: false,
				scope: encodeScope({ type: "apteka", id: idfirm }),
				deviceUuid,
			}),
		{
			success: (r) =>
				r.deliveredOnline
					? `Wysłano na ${name}`
					: `${name} offline — dostanie wiadomość po połączeniu (do ${VALIDITY_HOURS} h)`,
			error: "Nie udało się wysłać wiadomości",
			onSuccess: () => {
				setOpen(false);
				setBody("");
			},
		},
	);
	return (
		<>
			<Button
				size="icon"
				variant="outline"
				className="size-7 shrink-0"
				aria-label={`Wyślij wiadomość na ${name}`}
				title="Wyślij wiadomość na ekran stanowiska"
				onClick={() => setOpen(true)}
			>
				<Send className="size-3.5" />
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>Wiadomość na {name}</DialogTitle>
						<DialogDescription>
							Pokaże się na ekranie tego stanowiska. Offline — po połączeniu, do {VALIDITY_HOURS} h.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<form
							id={formId}
							className="space-y-2"
							onSubmit={(e) => {
								e.preventDefault();
								if (title.trim() && body.trim()) send.mutate(undefined);
							}}
						>
							<Input
								aria-label="Tytuł"
								maxLength={200}
								value={title}
								onChange={(e) => setTitle(e.target.value)}
							/>
							<textarea
								aria-label="Treść"
								autoFocus
								rows={4}
								maxLength={BODY_MAX}
								placeholder="Np. Proszę zrestartować komputer po zamknięciu sprzedaży."
								value={body}
								onChange={(e) => setBody(e.target.value)}
								className="w-full resize-y rounded-md border border-input bg-background p-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
							/>
						</form>
					</DialogBody>
					<DialogFooter>
						<Button variant="outline" onClick={() => setOpen(false)}>
							Anuluj
						</Button>
						<Button
							type="submit"
							form={formId}
							disabled={!title.trim() || !body.trim() || send.isPending}
						>
							Wyślij
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
